import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, TrashIcon, EyeIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface RecipeRef {
  _id: string;
  title?: string;
  titleArabic?: string;
}

interface ChefRef {
  _id: string;
  name: string;
  email?: string;
  avatar?: string;
}

interface CommunityRecipe {
  _id: string;
  userId: ChefRef;
  recipeId?: RecipeRef;
  title?: string;
  caption?: string;
  image?: string;
  video?: string;
  mediaType?: 'image' | 'video';
  likes: string[];
  savedBy?: string[];
  reactions: Array<{ userId: string; reactionType: string; createdAt: Date }>;
  comments: Array<{ _id: string; userId: string; comment: string; createdAt: Date }>;
  moderationStatus: 'pending' | 'approved' | 'rejected';
  isModerated: boolean;
  isArchived: boolean;
  isDraft?: boolean;
  isReported: boolean;
  reports: Array<{ userId: string; reason?: string; reportedAt: Date }>;
  createdAt: string;
  updatedAt: string;
}

export default function CommunityRecipesTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [recipes, setRecipes] = useState<CommunityRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'reported' | 'draft'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [processing, setProcessing] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<CommunityRecipe | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [mediaViewer, setMediaViewer] = useState<{ type: 'image' | 'video'; url: string } | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch, statusFilter]);

  useEffect(() => {
    loadRecipes();
  }, [currentPage, limit, activeSearch, statusFilter]);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number | boolean> = {
        page: currentPage,
        limit,
        includeArchived: true,
      };
      if (activeSearch) params.search = activeSearch;
      if (statusFilter === 'pending') params.moderationStatus = 'pending';
      if (statusFilter === 'approved') params.moderationStatus = 'approved';
      if (statusFilter === 'rejected') params.moderationStatus = 'rejected';
      if (statusFilter === 'reported') params.reportedOnly = 'true';
      if (statusFilter === 'draft') params.draftOnly = 'true';

      const response = await api.get('/admin/community-recipes', { params });
      setRecipes(response.data.recipes || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading community recipes:', error);
      showToast('Failed to load community recipes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    const el = document.createElement('div');
    el.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`;
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => document.body.removeChild(el), 3000);
  };

  const handleSearch = () => {
    setActiveSearch(search.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('communityRecipes.confirmDelete'))) return;
    try {
      setProcessing(true);
      await api.delete(`/community/recipes/${id}`);
      loadRecipes();
      showToast('Community recipe deleted successfully!', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to delete', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleModeration = async (id: string, status: 'approved' | 'rejected') => {
    try {
      setProcessing(true);
      await api.put(`/admin/community-recipes/${id}/moderate`, { moderationStatus: status });
      loadRecipes();
      showToast(`Community recipe ${status} successfully!`, 'success');
      if (selectedRecipe?._id === id) setShowModal(false);
    } catch (error: any) {
      showToast(error.response?.data?.message || `Failed to ${status}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSetDraft = async (id: string, isDraft: boolean) => {
    try {
      setProcessing(true);
      await api.put(`/admin/community-recipes/${id}/draft`, { isDraft });
      loadRecipes();
      showToast(isDraft ? 'Marked as draft' : 'Published', 'success');
      if (selectedRecipe?._id === id) setSelectedRecipe((r) => (r ? { ...r, isDraft } : null));
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to update draft status', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string, isReported: boolean, isArchived: boolean, isDraft?: boolean) => {
    if (isArchived) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{t('communityRecipes.archived')}</span>;
    }
    if (isDraft) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{t('communityRecipes.draft')}</span>;
    }
    if (isReported) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{t('communityRecipes.reported')}</span>;
    }
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t('communityRecipes.approved')}</span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{t('communityRecipes.rejected')}</span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{t('communityRecipes.pending')}</span>;
      default:
        return null;
    }
  };

  /** Recipe text is the caption */
  const recipeText = (r: CommunityRecipe) => r.caption || '—';

  if (loading && recipes.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('communityRecipes.loading')}</p>
      </div>
    );
  }

  const thClass = `px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`;

  return (
    <div className={`bg-white rounded-lg shadow ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="p-6 border-b border-gray-200">
        <div className={`flex justify-between items-center mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('communityRecipes.title')}</h3>
            <p className="text-sm text-gray-500">{t('communityRecipes.manageDescription')}</p>
          </div>
        </div>
        <div className="flex flex-col space-y-4">
          <div className={`flex flex-col md:flex-row gap-4 flex-wrap ${isRtl ? 'md:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-[200px] relative flex items-center">
              <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
              <input
                type="text"
                placeholder={t('communityRecipes.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-20' : 'pl-10 pr-20'}`}
              />
              <button
                onClick={handleSearch}
                className={`absolute px-4 py-1.5 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm transition-colors ${isRtl ? 'left-2 right-auto' : 'right-2'}`}
              >
                {t('common.search')}
              </button>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="all">{t('communityRecipes.allStatus')}</option>
              <option value="pending">{t('communityRecipes.pending')}</option>
              <option value="approved">{t('communityRecipes.approved')}</option>
              <option value="rejected">{t('communityRecipes.rejected')}</option>
              <option value="reported">{t('communityRecipes.reported')}</option>
              <option value="draft">{t('communityRecipes.draft')}</option>
            </select>
            {(activeSearch || statusFilter !== 'all') && (
              <button onClick={clearFilters} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                {t('common.clearFilters')}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={thClass}>{t('communityRecipes.chef')}</th>
              <th className={thClass}>{t('communityRecipes.recipe')}</th>
              <th className={thClass}>{t('communityRecipes.engagement')}</th>
              <th className={thClass}>{t('communityRecipes.status')}</th>
              <th className={thClass}>{t('communityRecipes.date')}</th>
              <th className={thClass}>{t('communityRecipes.actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {recipes.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  {t('communityRecipes.noRecipes')}
                </td>
              </tr>
            ) : (
              recipes.map((item) => (
                <tr key={item._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {item.userId?.avatar ? (
                          <img className="h-10 w-10 rounded-full" src={item.userId.avatar} alt={item.userId.name} />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-primary-orange flex items-center justify-center text-white font-semibold">
                            {item.userId?.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{item.userId?.name || '—'}</div>
                        <div className="text-sm text-gray-500">{item.userId?.email || item.userId?._id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate" title={item.caption || ''}>{recipeText(item)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div>❤️ {item.likes?.length ?? 0} likes</div>
                      <div>🔗 {item.savedBy?.length ?? 0} shares</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(item.moderationStatus, item.isReported, item.isArchived, item.isDraft)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{new Date(item.createdAt).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-500">{new Date(item.createdAt).toLocaleTimeString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => { setSelectedRecipe(item); setShowModal(true); }} className="text-primary-orange hover:text-orange-600 mr-4">
                      <EyeIcon className="w-5 h-5" />
                    </button>
                    {item.moderationStatus === 'pending' && (
                      <>
                        <button onClick={() => handleModeration(item._id, 'approved')} className="text-green-600 hover:text-green-700 mr-4" disabled={processing}>Approve</button>
                        <button onClick={() => handleModeration(item._id, 'rejected')} className="text-red-600 hover:text-red-700 mr-4" disabled={processing}>Reject</button>
                      </>
                    )}
                    <button onClick={() => handleSetDraft(item._id, !item.isDraft)} className="text-gray-600 hover:text-gray-800 mr-4" disabled={processing} title={item.isDraft ? 'Publish' : 'Mark as draft'}>
                      {item.isDraft ? 'Publish' : 'Mark as draft'}
                    </button>
                    <button onClick={() => handleDelete(item._id)} className="text-red-600 hover:text-red-700" disabled={processing}>
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <span className="text-sm text-gray-700">
          Showing {recipes.length > 0 ? (currentPage - 1) * limit + 1 : 0} to {Math.min(currentPage * limit, total)} of {total}
        </span>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {showModal && selectedRecipe && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Community Recipe Details</h3>
              <button onClick={() => { setShowModal(false); setSelectedRecipe(null); }} className="text-gray-400 hover:text-gray-500">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Chef</h4>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  {selectedRecipe.userId?.avatar ? (
                    <img className="h-12 w-12 rounded-full" src={selectedRecipe.userId.avatar} alt={selectedRecipe.userId.name} />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-primary-orange flex items-center justify-center text-white font-semibold">
                      {selectedRecipe.userId?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-900">{selectedRecipe.userId?.name}</div>
                    <div className="text-sm text-gray-500">{selectedRecipe.userId?.email}</div>
                  </div>
                </div>
              </div>
              {(selectedRecipe.image || selectedRecipe.video) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Media</h4>
                  <div
                    className="p-3 bg-gray-50 rounded-lg flex justify-center items-center min-h-[280px] cursor-pointer"
                    onClick={() => {
                      if (selectedRecipe.video) setMediaViewer({ type: 'video', url: selectedRecipe.video });
                      else if (selectedRecipe.image) setMediaViewer({ type: 'image', url: selectedRecipe.image });
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (selectedRecipe.video) setMediaViewer({ type: 'video', url: selectedRecipe.video });
                        else if (selectedRecipe.image) setMediaViewer({ type: 'image', url: selectedRecipe.image });
                      }
                    }}
                  >
                    {selectedRecipe.video ? (
                      <video src={selectedRecipe.video} controls onClick={(e) => e.stopPropagation()} className="max-h-80 rounded-lg w-full object-contain" />
                    ) : selectedRecipe.image ? (
                      <img src={selectedRecipe.image} alt="Recipe" className="max-h-80 rounded-lg object-contain" />
                    ) : null}
                  </div>
                </div>
              )}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Recipe</h4>
                <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">{recipeText(selectedRecipe)}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Engagement</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-900">{selectedRecipe.likes?.length ?? 0}</div>
                    <div className="text-sm text-gray-500">Likes</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-900">{selectedRecipe.savedBy?.length ?? 0}</div>
                    <div className="text-sm text-gray-500">Shares</div>
                  </div>
                </div>
              </div>
              {selectedRecipe.reports?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Reports ({selectedRecipe.reports.length})</h4>
                  <div className="space-y-2">
                    {selectedRecipe.reports.map((report, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded-lg">
                        <div className="text-sm text-gray-900">{report.reason || 'No reason provided'}</div>
                        <div className="text-xs text-gray-500 mt-1">Reported on {new Date(report.reportedAt).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-3 items-center">
                <button
                  onClick={() => { handleSetDraft(selectedRecipe._id, !selectedRecipe.isDraft); setShowModal(false); setSelectedRecipe(null); }}
                  disabled={processing}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  {selectedRecipe.isDraft ? 'Publish' : 'Mark as draft'}
                </button>
                {selectedRecipe.moderationStatus === 'pending' && (
                  <>
                    <button
                      onClick={() => { handleModeration(selectedRecipe._id, 'approved'); setShowModal(false); setSelectedRecipe(null); }}
                      disabled={processing}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => { handleModeration(selectedRecipe._id, 'rejected'); setShowModal(false); setSelectedRecipe(null); }}
                      disabled={processing}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {mediaViewer && (
        <div
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
          onClick={() => setMediaViewer(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Media viewer"
        >
          <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setMediaViewer(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 p-2"
              aria-label="Close"
            >
              <XCircleIcon className="w-8 h-8" />
            </button>
            {mediaViewer.type === 'video' ? (
              <video src={mediaViewer.url} controls autoPlay className="max-h-[85vh] max-w-full rounded-lg" />
            ) : (
              <img src={mediaViewer.url} alt="Recipe media" className="max-h-[85vh] max-w-full rounded-lg object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
